import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ParentComponent } from 'src/app/components/parent/parent.component';
import { FinancialsService } from "../../../../services/modules/financials/financials.service";
import { DashboardService } from "../../../../services/modules/dashboards/dashboard.service";
import { ActivatedRoute, Router } from "@angular/router";
import { InfoDialogComponent } from 'src/app/components/info-dialog/info-dialog.component';
import { SidebarService } from 'src/app/services/components/sidebar/sidebar.service';
import { UserModeService } from 'src/app/services/customs/user-mode/user-mode.service';
import { environment } from 'src/environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { distinctUntilChanged } from 'rxjs/operators';

interface Action {
  id: number;
  title: string;
  desc: string;
  disabled?: boolean;
  action_name: string;
  action_url: string;
}

interface KeyValues {
  title: string;
  value: number;
}

interface ValuesResponse {
  status: number;
  values: KeyValues[];
}
interface Help {
  title: string;
  desc: string;
  action_name: string;
}

interface Overview {
  wallet: number;
  booked_jobs: number;
  jobs_started: number;
  job_in_transit: number;
  jobs_completed: number;
  job_invitations: number;
  pending_cancelation: number;
  disputes: Array<{
    listing_id: string;
    reason_customer: { value: string; label: string };
    reason_provider: { value: string; label: string };
    status: string;
    date: string;
  }>;
  completion_rate: number;
}


@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent extends ParentComponent implements OnInit {
  @ViewChild('disputesModal') disputesModal!: TemplateRef<any>;
  public domain?: string;
  currentUserMode: 'customer' | 'provider' = 'customer';

  constructor(
    private dashboardService: DashboardService,
    private router: Router,
    private sidebarService: SidebarService,
    private userModeService: UserModeService,
    private cookieService: CookieService,
    private modalService: NgbModal,
    private route: ActivatedRoute
  ) {
    super();
    this.domain = `${environment.appUrl}`
  }

  public actions: Action[] = [];
  public keyValues: KeyValues[] = [];

  public overview: any;

  ngOnInit(): void {
    this.userModeService.mode$.pipe(distinctUntilChanged()).subscribe((mode) => {
      if (!mode) return; // no user yet

      this.currentUserMode = mode;
      console.log('Loaded mode from service:', mode);

      this.setActions();

      if (mode === 'customer') {
        this.getKeyValues();
      } else if (mode === 'provider') {
        console.log('Provider mode detected, fetching dashboard overview');
        this.getDashboardOverview();
      }
    });

    this.checkAccessDenied();
  }

  private checkAccessDenied() {
    this.route.queryParams.subscribe(params => {
      if (params['accessDenied']) {
        const modalRef = this.modalService.open(InfoDialogComponent, { centered: true });
        modalRef.componentInstance.headerTitle = 'Access Restricted';
        modalRef.componentInstance.headerIconStyle = 'text-warning me-2';
        modalRef.componentInstance.headerIcon = 'bi bi-exclamation-triangle-fill';
        modalRef.componentInstance.bodyText = "You don't have access to that page in your current mode.";
        modalRef.componentInstance.bodyText2 = "Please try switching your mode in the sidebar to access this feature.";
        modalRef.componentInstance.btnStyle = 'btn-primary';
        modalRef.componentInstance.btnText = 'Understood';

        // Clear query params
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { accessDenied: null },
          queryParamsHandling: 'merge'
        });
      }
    });
  }

  public help: Help = {
    title: 'Got complex freight?',
    desc: 'Speak to one our account managers to settle your shipping problems',
    action_name: 'talk to an expert',
  };

  public getHelp() {
    window.open('https://www.truckit.net/get-a-quote/projects', '_blank');
  }

  public setActions(): void {
    const shipAction: Action = {
      id: 1,
      title: 'Ready to ship',
      desc: 'Create a listing',
      action_name: 'Create a shipment',
      action_url: '/instant-quote',
      disabled: false
    };

    this.actions.push(shipAction);

  }

  private getKeyValues() {
    this.dashboardService.getKeyValues().subscribe(response => {
      const valuesResponse: ValuesResponse = response;
      if (valuesResponse.status === 200) {
        this.keyValues = valuesResponse.values;
      } else {

      }
    }, error => {

    });
  }

  redirectTo(url: string): void {
    this.router.navigateByUrl(url).finally(() => {
      this.sidebarService.setActiveTab();
    });

  }

  redirectToShipments(card: any): void {
    let tab: string;
    let filter: string;

    // Determine the tab and filter based on the card's title
    switch (card.title.toLowerCase()) {
      case 'active listings':
        tab = 'listings';
        filter = 'active';
        break;
      case 'booked listings':
        tab = 'bookings';
        filter = 'booked';
        break;
      case 'jobs in transit':
        tab = 'bookings';
        filter = 'in-transit';
        break;
      case 'jobs completed':
        tab = 'bookings';
        filter = 'complete';
        break;
      default:
        // Set defaults or handle unknown cases
        tab = 'listings'; // Replace with your actual default tab if necessary
        filter = 'all'; // Replace with your actual default filter if necessary
    }

    let encodedFilter = encodeURIComponent(filter);

    // Navigate with both tab and filter as query parameters
    this.router.navigate(
      ['/listings-and-bookings'],
      {
        queryParams:
          { tab: tab, filter: encodedFilter }
      }).finally(() => {
        this.sidebarService.setActiveTab();
      });

  }

  getDashboardOverview = () => {
    this.dashboardService.getDashboardOverview().subscribe(response => {
      if (response.status === 200) {
        this.overview = response.data;

        this.overview.disputes = this.overview.disputes?.map((d: any) => ({
          ...d,
          reason_customer: typeof d.reason_customer === 'string'
            ? JSON.parse(d.reason_customer)
            : d.reason_customer,
          reason_provider: typeof d.reason_provider === 'string'
            ? JSON.parse(d.reason_provider)
            : d.reason_provider,
          listing_entity: typeof d.listing_entity === 'string'
            ? JSON.parse(d.listing_entity)
            : d.listing_entity,
        })) || [];
      } else {
        // Handle error case
      }
    });
  }

  redirectToInvitation() {
    window.open(`${this.domain}/provider/invitations`, '_blank');
  }

  redirectToPendingJobs() {
    window.open(`${this.domain}/provider/jobs?pagetype=cancellation`, '_blank');
  }

  openDisputesModal() {
    this.modalService.open(this.disputesModal, { size: 'xl', centered: true });
  }

  formatDate(timestamp: number): string {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}
